import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faUserMinus, faMoneyBillTransfer, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import Layout from '../components/Layout';
import { useManageBot } from '../contexts/ManageBotContext';
import contactService from '../services/contactService';
import adminCommandService from '../services/adminCommandService';
import './AdminCommands.css';

const ContactSearchSelect = ({ contacts, value, onChange, id, disabled }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedContact = contacts.find(c => String(c.id) === String(value));

  const filtered = search.trim()
    ? contacts.filter(c => {
        const term = search.toLowerCase();
        return (
          (c.first_name && c.first_name.toLowerCase().includes(term)) ||
          (c.last_name && c.last_name.toLowerCase().includes(term)) ||
          (c.username && c.username.toLowerCase().includes(term)) ||
          (c.telegram_id && String(c.telegram_id).includes(term))
        );
      })
    : contacts;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getContactLabel = (contact) => {
    const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    const username = contact.username ? `@${contact.username}` : '';
    const tid = contact.telegram_id;
    return [name, username, `ID: ${tid}`].filter(Boolean).join(' - ');
  };

  const handleSelect = (contact) => {
    onChange(String(contact.id));
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setSearch('');
  };

  return (
    <div className="contact-search-select" ref={wrapperRef}>
      {selectedContact && !isOpen ? (
        <div className="contact-selected" onClick={() => !disabled && setIsOpen(true)}>
          <span className="contact-selected-label">{getContactLabel(selectedContact)}</span>
          <button type="button" className="contact-clear-btn" onClick={(e) => { e.stopPropagation(); handleClear(); }} disabled={disabled}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      ) : (
        <div className="contact-search-input-wrapper">
          <FontAwesomeIcon icon={faSearch} className="contact-search-icon" />
          <input
            id={id}
            type="text"
            className="contact-search-input"
            placeholder="Buscar por nome, @username ou ID..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
            autoComplete="off"
          />
        </div>
      )}
      {isOpen && (
        <div className="contact-search-dropdown">
          {filtered.length === 0 ? (
            <div className="contact-search-empty">Nenhum contato encontrado</div>
          ) : (
            filtered.slice(0, 50).map((contact) => (
              <div
                key={contact.id}
                className={`contact-search-option ${String(contact.id) === String(value) ? 'selected' : ''}`}
                onClick={() => handleSelect(contact)}
              >
                <span className="contact-option-name">
                  {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Sem nome'}
                </span>
                <span className="contact-option-details">
                  {contact.username ? `@${contact.username}` : ''} {contact.telegram_id ? `ID: ${contact.telegram_id}` : ''}
                </span>
              </div>
            ))
          )}
          {filtered.length > 50 && (
            <div className="contact-search-more">Refine a busca para ver mais resultados...</div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminCommands = () => {
  const navigate = useNavigate();
  const { botId } = useParams();
  const isInManageBot = useManageBot();

  const [contacts, setContacts] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [subContactId, setSubContactId] = useState('');
  const [subDays, setSubDays] = useState(30);
  const [removeContactId, setRemoveContactId] = useState('');
  const [refundContactId, setRefundContactId] = useState('');

  useEffect(() => {
    if (botId) {
      loadContacts();
    } else {
      setError('Bot nao selecionado. Por favor, selecione um bot primeiro.');
      setLoadingData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  const loadContacts = async () => {
    try {
      setLoadingData(true);
      setError('');
      const data = await contactService.getAllContacts(botId);
      setContacts(data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar contatos');
    } finally {
      setLoadingData(false);
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleManualSubscription = async () => {
    if (!subContactId) {
      setError('Selecione um contato');
      return;
    }
    if (!subDays || subDays < 1) {
      setError('Informe a quantidade de dias (minimo 1)');
      return;
    }

    const contact = contacts.find(c => String(c.id) === String(subContactId));
    const contactName = contact ? (contact.first_name || contact.username || contact.telegram_id) : subContactId;

    if (!window.confirm(`Criar assinatura manual de ${subDays} dias para ${contactName}?`)) {
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      const result = await adminCommandService.manualSubscription(botId, subContactId, subDays);
      setSuccess(result.message || 'Assinatura criada com sucesso!');
      setSubContactId('');
      setSubDays(30);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.contact_id?.[0] || 'Erro ao criar assinatura');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!removeContactId) {
      setError('Selecione um contato');
      return;
    }

    const contact = contacts.find(c => String(c.id) === String(removeContactId));
    const contactName = contact ? (contact.first_name || contact.username || contact.telegram_id) : removeContactId;

    if (!window.confirm(`Remover ${contactName} do grupo? O usuario perdera o acesso imediatamente.`)) {
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      const result = await adminCommandService.removeUser(botId, removeContactId);
      setSuccess(result.message || 'Usuario removido com sucesso!');
      setRemoveContactId('');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao remover usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!refundContactId) {
      setError('Selecione um contato');
      return;
    }

    const contact = contacts.find(c => String(c.id) === String(refundContactId));
    const contactName = contact ? (contact.first_name || contact.username || contact.telegram_id) : refundContactId;

    if (!window.confirm(`ATENCAO: Processar reembolso para ${contactName}? Esta acao e irreversivel. O pagamento sera devolvido e o usuario sera removido do grupo.`)) {
      return;
    }

    clearMessages();
    setLoading(true);
    try {
      const result = await adminCommandService.refundUser(botId, refundContactId);
      setSuccess(result.message || 'Reembolso processado com sucesso!');
      setRefundContactId('');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao processar reembolso');
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className="admin-commands-page">
      {loadingData ? (
        <div className="loading-container">Carregando...</div>
      ) : !botId ? (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Voltar ao Dashboard
          </button>
        </div>
      ) : (
        <div className="admin-commands-content">
          <div className="admin-commands-header">
            <h1>Comandos Admin</h1>
            <p>Execute comandos administrativos diretamente pelo dashboard. As mesmas funcionalidades dos comandos /assinatura_manual, /remover e /reembolso do Telegram.</p>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="admin-commands-cards">
            {/* Card 1 - Assinatura Manual */}
            <div className="admin-command-card">
              <h2>
                <FontAwesomeIcon icon={faUserPlus} />
                Assinatura Manual
              </h2>
              <p className="card-description">
                Cria uma assinatura manual para um contato, gerando um link de acesso ao grupo VIP sem necessidade de pagamento.
              </p>
              <div className="admin-command-form">
                <div className="form-group">
                  <label htmlFor="sub-contact">
                    Contato <span className="required">*</span>
                  </label>
                  <ContactSearchSelect contacts={contacts} value={subContactId} onChange={setSubContactId} id="sub-contact" disabled={loading} />
                </div>
                <div className="form-group form-group-days">
                  <label htmlFor="sub-days">
                    Dias <span className="required">*</span>
                  </label>
                  <input
                    id="sub-days"
                    type="number"
                    min="1"
                    value={subDays}
                    onChange={(e) => setSubDays(parseInt(e.target.value) || '')}
                    disabled={loading}
                  />
                </div>
                <button
                  className="btn-action btn-subscription"
                  onClick={handleManualSubscription}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faUserPlus} />
                  {loading ? 'Processando...' : 'Criar Assinatura'}
                </button>
              </div>
            </div>

            {/* Card 2 - Remover do Grupo */}
            <div className="admin-command-card">
              <h2>
                <FontAwesomeIcon icon={faUserMinus} />
                Remover do Grupo
              </h2>
              <p className="card-description">
                Remove um usuario do grupo VIP imediatamente. O usuario perdera o acesso ao grupo.
              </p>
              <div className="admin-command-form">
                <div className="form-group">
                  <label htmlFor="remove-contact">
                    Contato <span className="required">*</span>
                  </label>
                  <ContactSearchSelect contacts={contacts} value={removeContactId} onChange={setRemoveContactId} id="remove-contact" disabled={loading} />
                </div>
                <button
                  className="btn-action btn-remove"
                  onClick={handleRemoveUser}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faUserMinus} />
                  {loading ? 'Processando...' : 'Remover'}
                </button>
              </div>
            </div>

            {/* Card 3 - Reembolso */}
            <div className="admin-command-card">
              <h2>
                <FontAwesomeIcon icon={faMoneyBillTransfer} />
                Reembolso
              </h2>
              <p className="card-description">
                Processa o reembolso da ultima transacao do contato no gateway de pagamento e remove o usuario do grupo.
              </p>
              <div className="refund-warning">
                <strong>Atencao:</strong> Esta acao e irreversivel. O valor sera devolvido ao cliente e ele sera removido do grupo.
              </div>
              <div className="admin-command-form">
                <div className="form-group">
                  <label htmlFor="refund-contact">
                    Contato <span className="required">*</span>
                  </label>
                  <ContactSearchSelect contacts={contacts} value={refundContactId} onChange={setRefundContactId} id="refund-contact" disabled={loading} />
                </div>
                <button
                  className="btn-action btn-refund"
                  onClick={handleRefund}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faMoneyBillTransfer} />
                  {loading ? 'Processando...' : 'Processar Reembolso'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isInManageBot) {
    return content;
  }

  return <Layout>{content}</Layout>;
};

export default AdminCommands;
